// ============================================================================
// CLEANING PROGRESS PERSISTENCE HOOK
// Saves/loads cleaner progress to prevent reset when switching tabs
// ============================================================================

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCleanerRecord } from "@/hooks/useCleaner";
import { useCallback, useRef, useEffect } from "react";

interface CleaningProgress {
  id?: string;
  cleaning_task_id: string;
  cleaner_id: string;
  completed_tasks: string[];
  progress_percent: number;
  updated_at: string;
}

export const useCleaningProgress = (taskId: string | undefined) => {
  const { data: cleaner } = useCleanerRecord();
  const queryClient = useQueryClient();
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch existing progress
  const { data: progress, isLoading } = useQuery({
    queryKey: ["cleaning-progress", taskId, cleaner?.id],
    queryFn: async (): Promise<CleaningProgress | null> => {
      if (!taskId || !cleaner) return null;

      const { data, error } = await supabase
        .from("cleaning_progress_temp")
        .select("*")
        .eq("cleaning_task_id", taskId)
        .eq("cleaner_id", cleaner.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching cleaning progress:", error);
        return null;
      }

      if (data) {
        return {
          ...data,
          completed_tasks: (data.completed_tasks as string[]) || [],
          progress_percent: Number(data.progress_percent) || 0,
        };
      }
      return null;
    },
    enabled: !!taskId && !!cleaner,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
  });

  // Upsert progress mutation — update cache with setQueryData (no invalidate) to avoid refetch thrashing
  const upsertMutation = useMutation({
    mutationFn: async ({
      completedTasks,
      progressPercent,
    }: {
      completedTasks: string[];
      progressPercent: number;
    }) => {
      if (!taskId || !cleaner) throw new Error("Missing task or cleaner");

      const { error } = await supabase
        .from("cleaning_progress_temp")
        .upsert(
          {
            cleaning_task_id: taskId,
            cleaner_id: cleaner.id,
            completed_tasks: completedTasks,
            progress_percent: progressPercent,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "cleaning_task_id,cleaner_id",
          }
        );

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      if (!taskId || !cleaner) return;
      const key = ["cleaning-progress", taskId, cleaner.id] as const;
      queryClient.setQueryData<CleaningProgress | null>(key, (prev) => ({
        ...(prev ?? {}),
        id: (prev as CleaningProgress | undefined)?.id,
        cleaning_task_id: taskId,
        cleaner_id: cleaner.id,
        completed_tasks: variables.completedTasks,
        progress_percent: variables.progressPercent,
        updated_at: new Date().toISOString(),
      }));
    },
  });

  // Delete progress mutation (called on submission) — set cache to null (no invalidate)
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!taskId || !cleaner) return;

      const { error } = await supabase
        .from("cleaning_progress_temp")
        .delete()
        .eq("cleaning_task_id", taskId)
        .eq("cleaner_id", cleaner.id);

      if (error) throw error;
    },
    onSuccess: () => {
      if (!taskId || !cleaner) return;
      const key = ["cleaning-progress", taskId, cleaner.id] as const;
      queryClient.setQueryData<CleaningProgress | null>(key, null);
    },
  });

  // Debounced save function (for normal typing/toggling). Deps use cleaner?.id so callback is stable when cleaner object ref changes.
  const saveProgress = useCallback(
    (completedTasks: string[], progressPercent: number) => {
      if (!taskId || !cleaner) return;

      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new timer (500ms debounce)
      debounceTimer.current = setTimeout(() => {
        upsertMutation.mutate({ completedTasks, progressPercent });
      }, 500);
    },
    [taskId, cleaner?.id, upsertMutation]
  );

  // Save immediately (e.g. on tab switch / unmount so progress is not lost). Stable deps to avoid effect/cleanup loops in consumer.
  const saveProgressImmediate = useCallback(
    (completedTasks: string[], progressPercent: number) => {
      if (!taskId || !cleaner) return;
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      upsertMutation.mutate({ completedTasks, progressPercent });
    },
    [taskId, cleaner?.id, upsertMutation]
  );

  // Cleanup: flush pending debounced save on unmount so progress is not lost when switching tabs
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, []);

  // Delete progress (called after successful submission)
  const deleteProgress = useCallback(() => {
    deleteMutation.mutate();
  }, [deleteMutation]);

  // Reset progress (clear local and remote)
  const resetProgress = useCallback(() => {
    deleteMutation.mutate();
  }, [deleteMutation]);

  return {
    progress,
    isLoading,
    saveProgress,
    saveProgressImmediate,
    deleteProgress,
    resetProgress,
    isSaving: upsertMutation.isPending,
  };
};
